import { create } from "zustand";
import { i18n } from "../translate/i18n";
import api from "../services/api";

const useFlowStore = create((set, get) => ({
  // Estado del flujo
  flowId: null,
  nodes: [],
  edges: [],
  selectedNode: null,
  flowName: i18n.t("flowBuilder.defaultFlowName"),
  flowDescription: '',

  // Acciones
  setNodes: (nodes) => set({ nodes }),
  setEdges: (edges) => set({ edges }),

  addNode: (node) => set((state) => ({
    nodes: [...state.nodes, node]
  })),

  updateNode: (nodeId, data) => set((state) => ({
    nodes: state.nodes.map(node =>
      node.id === nodeId ? { ...node, data: { ...node.data, ...data } } : node
    )
  })),

  deleteNode: (nodeId) => set((state) => ({
    nodes: state.nodes.filter(node => node.id !== nodeId),
    edges: state.edges.filter(edge => edge.source !== nodeId && edge.target !== nodeId),
    selectedNode: state.selectedNode?.id === nodeId ? null : state.selectedNode
  })),

  duplicateNode: (nodeId) => set((state) => {
    const nodeToDuplicate = state.nodes.find(node => node.id === nodeId);
    if (!nodeToDuplicate) return state;

    const newNode = {
      ...nodeToDuplicate,
      id: `node-${Date.now()}`,
      position: {
        x: nodeToDuplicate.position.x + 50,
        y: nodeToDuplicate.position.y + 50
      },
      data: {
        ...nodeToDuplicate.data,
        label: `${nodeToDuplicate.data.label} ${i18n.t("flowBuilder.copySuffix")}`
      }
    };

    return { nodes: [...state.nodes, newNode] };
  }),

  setSelectedNode: (node) => set({ selectedNode: node }),
  setFlowId: (flowId) => set({ flowId }),

  setFlowName: (name) => set({ flowName: name }),
  setFlowDescription: (description) => set({ flowDescription: description }),

  // Guardar flujo
  saveFlow: async () => {
    const { flowId, nodes, edges, flowName, flowDescription } = get();

    try {
      let currentFlowId = flowId;

      if (!currentFlowId) {
        const { data } = await api.post("/flowbuilder", {
          name: flowName || i18n.t("flowBuilder.defaultFlowName"),
          flow_data: {
            nodes: nodes || [],
            connections: edges || [],
            description: flowDescription || "",
          },
        });

        currentFlowId = data?.id || data?.flowId || data?.flow?.id || null;
        if (!currentFlowId) {
          throw new Error("Flow ID missing after create");
        }
      } else {
        await api.put("/flowbuilder", {
          id: currentFlowId,
          name: flowName || i18n.t("flowBuilder.defaultFlowName"),
          flow_data: {
            nodes: nodes || [],
            connections: edges || [],
            description: flowDescription || "",
          },
        });
      }

      await api.post("/flowbuilder/flow", {
        idFlow: currentFlowId,
        nodes: nodes || [],
        connections: edges || [],
      });

      set({ flowId: currentFlowId });
      return { success: true, flowId: currentFlowId };
    } catch (error) {
      return { success: false, error };
    }
  },

  // Cargar flujo
  loadFlow: async (flowId) => {
    try {
      const targetFlowId = flowId || get().flowId;
      if (!targetFlowId) {
        throw new Error("Flow ID is required");
      }

      const { data } = await api.get(`/flowbuilder/flow/${targetFlowId}`);
      const flowPayload = data?.flow || {};
      const flowData = flowPayload?.flow || {};

      set({
        flowId: targetFlowId,
        flowName: flowPayload?.name || get().flowName,
        flowDescription: flowData?.description || '',
        nodes: flowData?.nodes || [],
        edges: flowData?.connections || [],
        selectedNode: null,
      });

      return { success: true, flowId: targetFlowId };
    } catch (error) {
      return { success: false, error };
    }
  },

  // Limpiar flujo
  clearFlow: () => set({
    flowId: null,
    nodes: [],
    edges: [],
    selectedNode: null,
    flowName: i18n.t("flowBuilder.defaultFlowName"),
    flowDescription: ''
  })
}));

export default useFlowStore;
